/**
 * Orders API Client
 *
 * HTTP REST API endpoints for Orders functionality:
 * - Create order (PUBLIC or DIRECT)
 * - Publish order
 * - Get order details
 * - Search orders
 * - Get my orders
 *
 * Uses HTTP-only cookies for authentication (via fetchWithTokenRefresh)
 */

import { buildApiUrl } from './config';
import { fetchWithTokenRefresh } from './refreshToken';
import { handleApiError } from './errorHandler';

// Order types matching backend
export type OrderType = 'PUBLIC' | 'DIRECT';
export type OrderStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'PENDING_REVIEW'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'DISPUTED'
  | 'ARCHIVED';

export interface CreateOrderRequest {
  title: string;
  description: string;
  type: OrderType;
  categoryId?: string; // Required for PUBLIC, optional for DIRECT
  // For DIRECT orders
  workerActorId?: string;
  clientActorId?: string;
  // Optional fields
  budget?: number;
  deadline?: string;
  // Image URLs (uploaded via /storage/public/gallery before order creation)
  images?: string[];
  // Location (optional for DIRECT)
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}

export interface OrderResponse {
  id: string;
  title: string;
  description: string;
  type: OrderType;
  status: OrderStatus;
  categoryId: string;
  clientActorId: string;
  workerActorId?: string;
  createdByUserId?: string; // Who created this order (for edit permissions)
  budget?: number;
  agreedPrice?: number;
  deadline?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  publishedAt?: string;
  startedAt?: string;
  completedAt?: string;
  lastActivityAt?: string;
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
  clientActor?: {
    id: string;
    userId: string;
    name: string;
    displayName?: string;
    avatarUrl?: string;
  };
  workerActor?: {
    id: string;
    userId: string;
    name: string;
    displayName?: string;
    avatarUrl?: string;
  };
  category?: {
    id: string;
    slug: string;
    nameEn: string;
    nameRu?: string;
  };
  _count?: {
    proposals: number;
  };
  // Image URLs attached to the order (served from /api/public/gallery/...)
  images?: string[];
}

/**
 * Orders API
 */
export const ordersApi = {
  /**
   * Create new order (DRAFT status)
   * POST /orders
   */
  async create(data: CreateOrderRequest): Promise<OrderResponse> {
    const url = buildApiUrl('/orders');
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle specific error codes
      if (errorData.code === 'EMAIL_NOT_VERIFIED') {
        throw new Error('Please verify your email before creating orders');
      }

      // Extract message from various error formats
      const message =
        errorData.message ||
        (typeof errorData === 'string' ? errorData : null) ||
        'Failed to create order';

      throw new Error(message);
    }

    return response.json();
  },

  /**
   * Publish order (DRAFT -> PUBLISHED)
   * POST /orders/:id/publish
   */
  async publish(orderId: string): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}/publish`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      const message =
        errorData.message ||
        (typeof errorData === 'string' ? errorData : null) ||
        'Failed to publish order';

      throw new Error(message);
    }

    return response.json();
  },

  /**
   * Accept direct order (PUBLISHED -> ASSIGNED)
   * POST /orders/:id/accept
   * Client accepts DIRECT order created by worker
   */
  async acceptDirectOrder(orderId: string): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}/accept`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      const message =
        errorData.message ||
        (typeof errorData === 'string' ? errorData : null) ||
        'Failed to accept order';

      throw new Error(message);
    }

    return response.json();
  },

  /**
   * Start order (ASSIGNED -> IN_PROGRESS)
   * POST /orders/:id/start
   * Worker starts assigned order
   */
  async startOrder(orderId: string): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}/start`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      const message =
        errorData.message ||
        (typeof errorData === 'string' ? errorData : null) ||
        'Failed to start order';

      throw new Error(message);
    }

    return response.json();
  },

  /**
   * Create and immediately publish order
   * Convenience method for DIRECT orders
   */
  async createAndPublish(data: CreateOrderRequest): Promise<OrderResponse> {
    const order = await this.create(data);
    return this.publish(order.id);
  },

  /**
   * Get order by ID
   * GET /orders/:id
   */
  async getById(orderId: string): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch order');
    }

    return response.json();
  },

  /**
   * Update order status
   * PATCH /orders/:id/status
   */
  async updateStatus(
    orderId: string,
    status: OrderStatus
  ): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}/status`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to update order status');
    }

    return response.json();
  },

  /**
   * Get my orders (as client or worker)
   * GET /orders/my-orders
   */
  async getMyOrders(role?: 'client' | 'worker'): Promise<OrderResponse[]> {
    const queryParams = new URLSearchParams();
    if (role) {
      queryParams.append('role', role);
    }

    const query = queryParams.toString();
    const url = buildApiUrl(`/orders/my-orders${query ? `?${query}` : ''}`);

    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch orders');
    }

    return response.json();
  },

  /**
   * Update order details
   * PATCH /orders/:id
   */
  async update(
    orderId: string,
    data: Partial<Omit<CreateOrderRequest, 'type' | 'categoryId'>>
  ): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to update order');
    }

    return response.json();
  },

  /**
   * Delete order
   * DELETE /orders/:id
   */
  async delete(orderId: string): Promise<void> {
    const url = buildApiUrl(`/orders/${orderId}`);
    const response = await fetchWithTokenRefresh(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to delete order');
    }
  },

  async renew(orderId: string): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}/renew`);
    const response = await fetchWithTokenRefresh(url, { method: 'POST' });
    if (!response.ok) {
      throw await handleApiError(response, 'Failed to renew order');
    }
    return response.json();
  },

  async restore(orderId: string): Promise<OrderResponse> {
    const url = buildApiUrl(`/orders/${orderId}/restore`);
    const response = await fetchWithTokenRefresh(url, { method: 'POST' });
    if (!response.ok) {
      throw await handleApiError(response, 'Failed to restore order');
    }
    return response.json();
  },
};
